import { Loader2 } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import type { ChatReadReceipt, TeamChatMessage } from '../../lib/teamChatApi';
import { MessageItem } from './MessageItem';

const GROUP_WINDOW_MS = 5 * 60 * 1000;

function dayKey(iso: string): string {
  return new Date(iso).toDateString();
}

function formatDay(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Chronological message column with Discord-style author grouping and day
 * separators. Bottom-anchored: sticks to the newest message unless the user
 * scrolled up; scrolling to the top loads the previous page while preserving
 * the scroll position.
 */
export function MessageList({
  messages,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  meId,
  isAdmin,
  receipts,
  isGroup,
  onReply,
  onJumpTo,
}: {
  messages: TeamChatMessage[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  meId: string | undefined;
  isAdmin: boolean;
  /** Read watermarks for the "seen" ticks on own messages. */
  receipts: ChatReadReceipt[] | undefined;
  isGroup: boolean;
  onReply: (message: TeamChatMessage) => void;
  /** Owned by the parent: reaching an older message may need more pages. */
  onJumpTo: (messageId: string) => void;
}) {
  const { t, locale } = useI18n();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const prevHeightRef = useRef<number | null>(null);
  const latestId = messages.length > 0 ? messages[messages.length - 1].id : null;

  // Keep the view glued to the newest message unless the user scrolled up.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (prevHeightRef.current !== null) {
      // An older page just rendered — keep the viewport where it was.
      el.scrollTop += el.scrollHeight - prevHeightRef.current;
      prevHeightRef.current = null;
    } else if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [latestId, messages.length]);

  // Load older messages when scrolled near the top.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      stickToBottomRef.current = nearBottom;
      if (el.scrollTop < 60 && hasNextPage && !isFetchingNextPage) {
        prevHeightRef.current = el.scrollHeight;
        fetchNextPage();
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto pb-4">
      {isFetchingNextPage && (
        <div className="flex justify-center py-2">
          <Loader2 size={18} className="animate-spin text-muted-foreground" />
        </div>
      )}
      {messages.length === 0 && (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          {t('teamChat.emptyConversation')}
        </p>
      )}
      {messages.map((message, i) => {
        const prev = i > 0 ? messages[i - 1] : null;
        const newDay = !prev || dayKey(prev.createdAt) !== dayKey(message.createdAt);
        const showHeader =
          newDay ||
          !prev ||
          prev.author.id !== message.author.id ||
          message.replyTo !== null ||
          new Date(message.createdAt).getTime() - new Date(prev.createdAt).getTime() >
            GROUP_WINDOW_MS;
        return (
          <div key={message.id}>
            {newDay && (
              <div className="my-4 flex items-center gap-3 px-4">
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs font-medium text-muted-foreground">
                  {formatDay(message.createdAt, locale)}
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>
            )}
            <MessageItem
              message={message}
              meId={meId}
              isAdmin={isAdmin}
              showHeader={showHeader}
              receipts={receipts}
              isGroup={isGroup}
              onReply={onReply}
              onJumpTo={onJumpTo}
            />
          </div>
        );
      })}
    </div>
  );
}
